import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker, Drawer, FormField, Radio } from "@outpave/ds";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import type { FormikProps } from "formik";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { ADD_FUNDS, ADD_AUTO_FUNDING } from "../../../graphql/account";
import autoFunding from "../../../validations/autoFunding";
import { buildNextTransfers } from "../../../utils/helper";
import { formatCashFull } from "../../../utils/formatCash";
import { BankAccount } from "hooks/useGetBankAccounts";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  AccountSelect,
  AmountField,
  ConfirmTransfer,
  MemoField,
  TransferAccountsRow,
} from "./TransferFormFields";

interface FormikAddFundingDrawerProps {
  amount: string;
  transferDate?: Dayjs;
  comment: string;
  isRepeatJob: boolean;
  confirmTransfer: boolean;
  repeatOnDay?: boolean;
  repeatDayOfMonth?: string | number;
  repeatDayOfWeek?: string | number;
  repeatWeekday?: string | number;
  repeatNeverEnd: boolean;
  repeatEndOn?: Dayjs;
}

interface AddFundingDrawerProps {
  selectedBankAccountId?: string;
  open: boolean;
  handleCloseDrawer: (isReloadData: boolean) => void;
  toAccountsData: BankAccount[];
  bankAccountsData: BankAccount[];
  refetchAccounts: () => void;
}

/** How many upcoming transfers the repeat schedule previews. */
const TRANSFER_PREVIEW_COUNT = 5;

/** Days of the month a repeat can land on. */
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);

const AddFundingDrawer: React.FC<AddFundingDrawerProps> = (props) => {
  const {
    selectedBankAccountId,
    open,
    handleCloseDrawer,
    toAccountsData,
    bankAccountsData,
    refetchAccounts,
  } = props;

  const { t } = useTranslation();
  const [fundingAccount, setFundingAccount] = useState("");
  const [toAccount, setToAccount] = useState("");

  const [addFunds, { loading: loadingAddFunds }] = useMutation(ADD_FUNDS, {
    onCompleted: () => {
      toast.success(t("TransferProcessSuccessMessage"));
      formik.resetForm();
      handleCloseDrawer(true);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const [addAutoFunding, { loading: loadingAutoFunding }] = useMutation(
    ADD_AUTO_FUNDING,
    {
      onCompleted: () => {
        toast.success(t("TransferScheduleSuccessMessage"));
        formik.resetForm();
        handleCloseDrawer(true);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  );

  const formik = useFormik<FormikAddFundingDrawerProps>({
    initialValues: {
      amount: "",
      transferDate: dayjs(new Date()),
      comment: "",
      isRepeatJob: false,
      confirmTransfer: false,
      repeatOnDay: true,
      repeatDayOfMonth: "",
      repeatDayOfWeek: "",
      repeatWeekday: "",
      repeatNeverEnd: true,
      repeatEndOn: undefined,
    },
    validationSchema: autoFunding,
    onSubmit: async (values) => {
      const {
        amount,
        comment,
        confirmTransfer,
        transferDate,
        isRepeatJob,
        repeatOnDay,
        repeatDayOfMonth,
        repeatDayOfWeek,
        repeatWeekday,
        repeatNeverEnd,
        repeatEndOn,
      } = values;

      if (!fundingAccount) {
        toast.error(t("Please select a funding account"));
        return;
      }

      if (!confirmTransfer) {
        toast.error(t("Please confirm the transfer"));
        return;
      }

      if (dayjs(transferDate).isBefore(dayjs().endOf("day")) && !isRepeatJob) {
        await addFunds({
          variables: {
            input: {
              fromAccountId: fundingAccount,
              toAccountId: toAccount,
              amount,
              memo: comment,
            },
          },
        });
      } else {
        const transferDates = buildNextTransfers(
          values,
          TRANSFER_PREVIEW_COUNT,
        );
        const input = {
          amount,
          transferDate: dayjs(transferDate).format("YYYY-MM-DD"),
          isRepeatJob,
          repeatOnDay,
          ...(repeatDayOfMonth !== "" && { repeatDayOfMonth }),
          ...(repeatDayOfWeek !== "" && { repeatDayOfWeek }),
          ...(repeatWeekday !== "" && { repeatWeekday }),
          repeatNeverEnd,
          ...(repeatEndOn &&
            repeatEndOn.isValid() && {
              repeatEndOn: dayjs(repeatEndOn).format("YYYY-MM-DD"),
            }),
          firstTransferDate:
            isRepeatJob && transferDates.length > 0
              ? dayjs(transferDates[0]).format("YYYY-MM-DD")
              : dayjs(transferDate).format("YYYY-MM-DD"),
          comment,
        };

        await addAutoFunding({
          variables: {
            fromAccountId: fundingAccount,
            toAccountId: toAccount,
            input,
          },
        });
      }
    },
  });

  const isShowRepeatSchedule = useMemo(() => {
    return formik.values.isRepeatJob;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.isRepeatJob, open]);

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bankAccountsData?.length > 0) {
      const selected = bankAccountsData.some(
        (account) => account.id === selectedBankAccountId,
      )
        ? selectedBankAccountId
        : bankAccountsData[0]?.id;
      setFundingAccount(selected || "");
    }
  }, [selectedBankAccountId, bankAccountsData]);

  useEffect(() => {
    if (toAccountsData?.length > 0 && toAccount === "") {
      setToAccount(toAccountsData[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toAccountsData?.[0]?.id, toAccount]);

  const busy = loadingAddFunds || loadingAutoFunding;

  const handleClose = () => {
    if (busy) return;
    formik.resetForm();
    handleCloseDrawer(false);
  };

  const errorFor = (field: keyof FormikAddFundingDrawerProps) =>
    formik.touched[field] ? (formik.errors[field] as string) : null;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Add Funds")}
      width="544px"
      loading={busy}
      footer={
        <>
          <Button variant="primary-outlined" onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            // No `isValid` gate: `handleSubmit` validates, touches every
            // field so the errors paint, and refuses to submit.
            disabled={!formik.values.confirmTransfer || busy}
            onClick={() => formik.handleSubmit()}
          >
            {busy ? t("Adding Funds") : t("Complete")}
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack gap="var(--space-6)">
          <TransferAccountsRow
            from={
              <AccountSelect
                name="fundingAccount"
                label={t("From")}
                value={fundingAccount}
                accounts={bankAccountsData}
                onSelect={setFundingAccount}
                onAccountAdded={setFundingAccount}
                refetchAccounts={refetchAccounts}
              />
            }
            to={
              <AccountSelect
                name="toAccount"
                label={t("To")}
                value={toAccount}
                accounts={toAccountsData}
                onSelect={setToAccount}
              />
            }
          />

          <AmountField
            value={formik.values.amount}
            onChange={(raw) => formik.setFieldValue("amount", raw)}
            helperText={t("Funds availabe within 1-2 business days")}
            errorText={errorFor("amount")}
          />

          <Stack gap="var(--space-3)" sx={{ maxWidth: 320 }}>
            <FormField
              label={
                isShowRepeatSchedule ? t("Start Date") : t("Transfer Date")
              }
              required
              helperText={t("Select the date you would like these funds sent")}
              errorText={errorFor("transferDate")}
            >
              <DatePicker
                size="medium"
                value={formik.values.transferDate ?? null}
                error={!!errorFor("transferDate")}
                onBlur={() => formik.setFieldTouched("transferDate", true)}
                onChange={(value) => {
                  if (!value) return;
                  formik.setFieldValue("transferDate", value);
                  if (!formik.values.isRepeatJob) {
                    formik.setFieldValue("repeatDayOfMonth", value.date());
                    formik.setFieldValue("repeatWeekday", value.day());
                    let firstWeekdayOfMonth = value
                      .clone()
                      .startOf("month")
                      .day(value.day());
                    if (firstWeekdayOfMonth.date() > 7) {
                      firstWeekdayOfMonth = firstWeekdayOfMonth.add(7, "day");
                    }
                    formik.setFieldValue(
                      "repeatDayOfWeek",
                      (value.date() - firstWeekdayOfMonth.date()) / 7 + 1,
                    );
                  }
                }}
              />
            </FormField>

            <Inline gap="var(--space-2)" wrap={false}>
              <Switch
                name="isRepeatJob"
                onChange={(_event, checked) => {
                  formik.setFieldValue("isRepeatJob", checked);
                  if (
                    checked &&
                    !dayjs(formik.values.transferDate).isAfter(
                      dayjs().endOf("day"),
                    )
                  ) {
                    formik.setFieldValue("transferDate", dayjs().add(1, "day"));
                  }
                }}
                checked={formik.values.isRepeatJob}
              />
              <Typography variant="subtitle2">
                {t("settingAutoFunding.repeatTransfer")}
              </Typography>
            </Inline>
          </Stack>

          {isShowRepeatSchedule && (
            <RepeatSchedule
              formik={formik}
              currency={
                toAccountsData.find(({ id }) => id === toAccount)?.currency
              }
            />
          )}

          <MemoField
            value={formik.values.comment}
            onChange={(comment) => formik.setFieldValue("comment", comment)}
          />

          <ConfirmTransfer
            checked={formik.values.confirmTransfer}
            onChange={(checked) =>
              formik.setFieldValue("confirmTransfer", checked)
            }
            errorText={errorFor("confirmTransfer")}
            // Add Funds pulls from the customer's bank, so the debit
            // authorization is the right words here. Withdraw pays out to that
            // bank and deliberately keeps the neutral default.
            label={t("AchDebitAuthorization")}
          />
        </Stack>
      </form>
    </Drawer>
  );
};

interface RepeatScheduleProps {
  formik: FormikProps<FormikAddFundingDrawerProps>;
  /** The destination account's, since that is what the amount lands in. */
  currency?: string;
}

/** The recurrence rule, and the next few dates it produces. */
const RepeatSchedule: React.FC<RepeatScheduleProps> = ({
  formik,
  currency = "usd",
}) => {
  const { t } = useTranslation();

  const isRepeatOnDay = !!formik.values.repeatOnDay;
  const isRepeatNeverEnd = formik.values.repeatNeverEnd;

  const transferDates = useMemo(
    () => buildNextTransfers(formik.values, TRANSFER_PREVIEW_COUNT),
    [formik.values],
  );

  // Weekday names in the active locale, Sunday first, matching the `day()`
  // index the form stores.
  const weekdays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) =>
        dayjs().day(index).format("dddd"),
      ),
    [],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
        padding: "var(--space-5)",
      }}
    >
      <Typography variant="subtitle1">
        {t("settingAutoFunding.repeatOn")}
      </Typography>

      <Inline gap="var(--space-3)" wrap={false}>
        <Radio
          checked={isRepeatOnDay}
          onChange={() => {
            formik.setFieldValue("repeatOnDay", true);
          }}
          value="day"
          name="repeatOnDay"
          slotProps={{ input: { "aria-label": t("settingAutoFunding.day") } }}
        />
        <Typography variant="body2">{t("settingAutoFunding.day")}</Typography>
        <Select
          size="small"
          name="repeatDayOfMonth"
          value={formik.values.repeatDayOfMonth}
          onChange={formik.handleChange}
          disabled={!isRepeatOnDay}
          error={
            !!formik.errors.repeatDayOfMonth &&
            !!formik.touched.repeatDayOfMonth
          }
          sx={{ minWidth: 96 }}
        >
          {DAYS_OF_MONTH.map((day) => (
            <MenuItem key={day} value={day}>
              {day}
            </MenuItem>
          ))}
        </Select>
      </Inline>

      <Inline gap="var(--space-3)" wrap={false}>
        <Radio
          checked={!isRepeatOnDay}
          onChange={() => {
            formik.setFieldValue("repeatOnDay", false);
          }}
          value="the"
          name="repeatOnDay"
          slotProps={{ input: { "aria-label": t("settingAutoFunding.the") } }}
        />
        <Typography variant="body2">{t("settingAutoFunding.the")}</Typography>
        <Select
          size="small"
          name="repeatDayOfWeek"
          value={formik.values.repeatDayOfWeek}
          onChange={formik.handleChange}
          disabled={isRepeatOnDay}
          error={
            !!formik.errors.repeatDayOfWeek && !!formik.touched.repeatDayOfWeek
          }
          sx={{ minWidth: 96 }}
        >
          <MenuItem value={1}>1st</MenuItem>
          <MenuItem value={2}>2nd</MenuItem>
          <MenuItem value={3}>3rd</MenuItem>
          <MenuItem value={4}>4th</MenuItem>
          <MenuItem value={5}>5th</MenuItem>
        </Select>
        <Select
          size="small"
          name="repeatWeekday"
          value={formik.values.repeatWeekday}
          onChange={formik.handleChange}
          disabled={isRepeatOnDay}
          error={
            !!formik.errors.repeatWeekday && !!formik.touched.repeatWeekday
          }
          sx={{ minWidth: 140 }}
        >
          {weekdays.map((weekday, index) => (
            <MenuItem key={weekday} value={index}>
              {weekday}
            </MenuItem>
          ))}
        </Select>
      </Inline>

      <Typography variant="subtitle1">
        {t("settingAutoFunding.ends")}
      </Typography>

      <Inline gap="var(--space-3)" align="start" wrap={false}>
        <Radio
          checked={!isRepeatNeverEnd}
          onChange={() => {
            formik.setFieldValue("repeatNeverEnd", false);
          }}
          value="end-on"
          name="repeatNeverEnd"
          slotProps={{ input: { "aria-label": t("settingAutoFunding.on") } }}
        />
        <Typography variant="body2" sx={{ paddingTop: "var(--space-2)" }}>
          {t("settingAutoFunding.on")}
        </Typography>
        <FormField
          errorText={
            formik.touched.repeatEndOn
              ? (formik.errors.repeatEndOn as string)
              : null
          }
          disabled={isRepeatNeverEnd}
        >
          <DatePicker
            size="medium"
            value={formik.values.repeatEndOn ?? null}
            onChange={(value) => formik.setFieldValue("repeatEndOn", value)}
            onBlur={() => formik.setFieldTouched("repeatEndOn", true)}
            error={!!(formik.touched.repeatEndOn && formik.errors.repeatEndOn)}
            disabled={isRepeatNeverEnd}
            minDate={formik.values.transferDate ?? dayjs()}
          />
        </FormField>
      </Inline>

      <Inline gap="var(--space-3)" wrap={false}>
        <Radio
          checked={isRepeatNeverEnd}
          onChange={() => {
            formik.setFieldValue("repeatNeverEnd", true);
          }}
          value="never"
          name="repeatNeverEnd"
          slotProps={{ input: { "aria-label": t("settingAutoFunding.never") } }}
        />
        <Typography variant="body2">{t("settingAutoFunding.never")}</Typography>
      </Inline>

      <Table size="small">
        <TableBody>
          {transferDates.map((date) => (
            <TableRow key={date.toISOString()}>
              <TableCell>{dayjs(date).format("MMM DD ddd")}</TableCell>
              <TableCell align="right">
                {formatCashFull(Number(formik.values.amount), currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default AddFundingDrawer;
